/* This store is persisted to localStorage automatically.
 * This is mainly a safeguard for page reloads. */

import eventStatusConstants from '@/data/event-constants/eventStatusConstants';
import { omit } from 'lodash';
import MutationTypes from '../constants/mutations';
import ActionTypes from '../constants/actions';
import GetterTypes from '../constants/getters';

const initialState = () => ({
  lastUnauthorizedPath: null,
  basicInfo: { userName: '' },
  authority: {},
  moduleCategories: [],
  breadcrumbs: '',
  userName: '',
  base64Avatar: '',
  searchableInfo: {},
  eventUnderEdit: null,
  showUnderEdit: null,
  masterArenas: [],
  eventCategories: [],
  theme: null,
});

export default {
  state: initialState,
  getters: {
    [GetterTypes.LAST_UNAUTHORIZED_PATH]: (state) => state.lastUnauthorizedPath,
    [GetterTypes.HAS_UNAUTHORIZED_LOGIN_ATTEMPT]: (state) => state.lastUnauthorizedPath !== null,
    [GetterTypes.IS_GRANTED]: (state) => (moduleKey) => {
      const foundModule = state.moduleCategories
        .flatMap((category) => category.modules)
        .filter((module) => module.authority === 'granted')
        .flatMap((module) => module.subAuthorityModules)
        .find((subModule) => subModule.key === moduleKey && subModule.authority === 'granted');

      return !!foundModule;
    },
    [GetterTypes.USER_NAME]: (state) => state.userName,
    [GetterTypes.USER_FIRST_NAME]: (state) => (state.userName
      ? state.userName.replace(/ .*/, '')
      : ''),
    [GetterTypes.PACKAGE_NAME]: (state) => state.basicInfo.package,
    [GetterTypes.ORGANIZER_NAME]: (state) => state.basicInfo.organizerName,
    [GetterTypes.USER_ID]: (state) => state.basicInfo.organizerUserId,
    [GetterTypes.ORGANIZER_ID]: (state) => state.basicInfo.organizerId,
    [GetterTypes.ORGANIZATION_NUMBER]: (state) => state.basicInfo.orgNr,
    [GetterTypes.DEFAULT_REPURCHASE]: (state) => state.basicInfo.defaultRepurchase,
    [GetterTypes.VAT_NUMBER]: (state) => {
      if (!state.basicInfo.orgNr) {
        return '';
      }

      return `SE${state.basicInfo.orgNr.replace('-', '')}01`;
    },
    [GetterTypes.EMAIL]: (state) => state.basicInfo.email,
    [GetterTypes.PHONE_NUMBER]: (state) => state.basicInfo.phoneNumber,
    [GetterTypes.PRICING_CATEGORIES]: (state) => state.basicInfo.categories,
    [GetterTypes.authority]: (state) => state.authority,
    [GetterTypes.MODULE_CATEGORIES]: (state) => state.moduleCategories,
    [GetterTypes.ARE_MODULES_ALREADY_LOADED]: (state) => state.moduleCategories.length,
    [GetterTypes.IS_BASIC_INFO_ALREADY_LOADED]: (state) => !!state.basicInfo.email,
    [GetterTypes.MODULE_CATEGORIES_WITH_GRANTED_MODULES]: (state) => Object.values(state.moduleCategories)
      .filter((category) => category.modules
        .some((module) => module.authority === 'granted' || module.authority === 'teaser')),
    [GetterTypes.SETTINGS_MODULE]: (state) => state.moduleCategories
      .flatMap((moduleCategory) => moduleCategory.modules)
      .find((module) => module.moduleName === 'Settings'),
    [GetterTypes.SUB_MODULES]: (state, getters) => getters
      .moduleCategories
      .flatMap((moduleCategory) => moduleCategory.modules.map(((module) => ({
        ...module,
        moduleCategory,
      }))))
      .flatMap((module) => module.subAuthorityModules.map((subAuthorityModule) => ({
        ...subAuthorityModule,
        module,
      }))),
    [GetterTypes.GRANTED_SUB_MODULES]: (state, getters) => getters[GetterTypes.SUB_MODULES].filter((subModule) => subModule.authority === 'granted'),
    [GetterTypes.BREADCRUMBS]: (state) => state.breadcrumbs,
    [GetterTypes.BASE64_AVATAR]: (state) => state.base64Avatar,
    [GetterTypes.CACHED_EVENTS]: (state) => state.searchableInfo.events,
    [GetterTypes.ACTIVE_AND_DRAFT_CACHED_EVENTS]: (state, getters) => getters[GetterTypes.CACHED_EVENTS]
      .filter((event) => [eventStatusConstants.STATUS_ACTIVE.key, eventStatusConstants.STATUS_DRAFT.key].includes(event.statusType)),
    [GetterTypes.EVENT_UNDER_EDIT]: (state) => state.eventUnderEdit,
    [GetterTypes.EVENT_UNDER_EDIT_ID]: (state) => (state.eventUnderEdit
      ? state.eventUnderEdit.id
      : null),
    [GetterTypes.SHOW_UNDER_EDIT]: (state) => state.showUnderEdit,
    [GetterTypes.SHOW_UNDER_EDIT_ID]: (state) => (state.showUnderEdit
      ? state.showUnderEdit.id
      : null),
    [GetterTypes.MASTER_ARENAS]: (state) => state.masterArenas,
    [GetterTypes.EVENT_CATEGORIES]: (state) => state.eventCategories,
    [GetterTypes.THEME]: (state) => state.theme,
  },
  mutations: {
    [MutationTypes.SET_LAST_UNAUTHORIZED_PATH]: (state, url) => {
      state.lastUnauthorizedPath = url;
    },
    [MutationTypes.CLEAR_LAST_UNAUTHORIZED_PATH]: (state) => {
      state.lastUnauthorizedPath = null;
    },
    [MutationTypes.SET_BASIC_INFO]: (state, info) => {
      state.basicInfo = info;
    },
    [MutationTypes.SET_BASE64_AVATAR]: (state, base64Avatar) => {
      state.base64Avatar = base64Avatar;
    },
    [MutationTypes.SET_USERNAME]: (state, userName) => {
      state.userName = userName;
    },
    [MutationTypes.SET_AUTHORITY]: (state, authority) => {
      state.authority = authority;
    },
    [MutationTypes.SET_MODULE_CATEGORIES]: (state, moduleCategories) => {
      state.moduleCategories = moduleCategories;
    },
    [MutationTypes.SET_BREADCRUMBS]: (state, breadcrumbs) => {
      state.breadcrumbs = breadcrumbs;
    },
    [MutationTypes.SET_MODULE_BREADCRUMBS]: (state, subModuleKey) => {
      state.moduleCategories.forEach((category) => {
        category.modules.forEach((module) => {
          module.subAuthorityModules.forEach((subModule) => {
            if (subModuleKey === subModule.key) {
              state.breadcrumbs = [
                { name: category.categoryName, type: 'Category', numberOfChildren: category.modules.length },
                {
                  name: module.moduleName, key: module.key, type: 'Module', numberOfChildren: module.subAuthorityModules.length,
                },
                { name: subModule.moduleName, type: 'Submodule' },
              ];
            }
          });
        });
      });
    },
    [MutationTypes.ADD_EXTRA_BREADCRUMB]: (state, extraCrumb) => {
      state.breadcrumbs.push(extraCrumb);
    },
    [MutationTypes.SET_SEARCHABLE_INFO]: (state, searchableInfo) => {
      state.searchableInfo = searchableInfo;
    },
    [MutationTypes.SET_EVENT_UNDER_EDIT]: (state, event) => {
      state.eventUnderEdit = event;
      state.showUnderEdit = null;
    },
    [MutationTypes.SET_SHOW_UNDER_EDIT]: (state, show) => {
      state.showUnderEdit = show;
    },
    [MutationTypes.SET_MASTER_ARENAS]: (state, masterArenas) => {
      state.masterArenas = masterArenas.map((arena) => ({
        ...arena,
        isNorticArena: true,
      }));
    },
    [MutationTypes.SIGN_OUT]: (state) => {
      const omitFromReset = ['theme'];
      const resetState = omit(initialState(), omitFromReset);

      Object.assign(state, resetState);
    },
    [MutationTypes.SET_EVENT_CATEGORIES]: (state, eventCategories) => {
      state.eventCategories = eventCategories;
    },
    [MutationTypes.SET_THEME]: (state, theme) => {
      state.theme = theme;
    },
  },
  actions: {
    [ActionTypes.REFRESH_CACHED_EVENTS]({ commit }) {
      return this.$axios.get('get_searchable_info')
        .then(({ data }) => {
          commit(MutationTypes.SET_SEARCHABLE_INFO, data);
        });
    },
    [ActionTypes.LOAD_MASTER_ARENAS]({ commit, state }) {
      if (!state.masterArenas?.length) {
        return this.$axios.get('get_masterarenas')
          .then(({ data }) => {
            commit(MutationTypes.SET_MASTER_ARENAS, data);
            return state.masterArenas;
          });
      }
      return Promise.resolve(state.masterArenas);
    },
  },
};
